import React, { useState, useEffect, useRef, useMemo } from "react";
import { useBusiness } from "../contexts/BusinessContext";
import { useUser } from '../contexts/UserContext';
import { MessageCircle, Mail, Phone, Clock, User, Edit, Plus, ChevronDown, Building2, Calendar, CheckCircle, FileText, Eye } from "lucide-react";
import { supabase } from "../lib/supabase";
import EditCustomerModal from "../components/manage/EditCustomerModal";
import CustomerModal from "../components/CustomerModal";
import BookingInfoModal from "../components/BookingInfoModal";
import ProposalDetailsModal from "../components/ProposalDetailsModal";
import { Customer } from "../lib/customers";
import { sendSMS } from "../lib/messaging";
import { sendGmailEmail } from "../lib/email";
import { EmailReplyComposer } from "../components/email/EmailReplyComposer";
import { parseEmailContent, getEmailPreview } from "../lib/emailParser";
import { getCustomerMilestones, BookingMilestone } from "../lib/bookingMilestones";
import { getBooking, Booking } from "../lib/bookings";
import { getProposal, Proposal } from "../lib/proposals";

interface Message {
  id: string;
  business_id: string;
  customer_id: string | null;
  message_type: 'sms' | 'email';
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  body: string;
  subject?: string;
  read: 'read' | 'unread';
  created_at: string;
  updated_at: string;
}

interface CommunicationCard {
  id: string; // unique identifier for grouping
  type: 'customer' | 'phone' | 'email';
  customer_id?: string;
  customer_name?: string;
  contact_info: string; // phone or email
  messages: Message[];
  last_message: Message;
  unread_count: number;
  has_unread: boolean;
}

// Timeline item that can be either a message or milestone
interface TimelineItem {
  id: string;
  type: 'message' | 'milestone';
  created_at: string;
  message?: Message;
  milestone?: BookingMilestone;
}

export default function Messages() {
  const { selectedBusiness } = useBusiness();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<'unread' | 'read'>('unread');
  const [messages, setMessages] = useState<Message[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [communicationCards, setCommunicationCards] = useState<CommunicationCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState<CommunicationCard | null>(null);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState<Customer | null>(null);
  const [createCustomerInitialData, setCreateCustomerInitialData] = useState<{ phone?: string; email?: string }>({});

  // Track locally marked-as-read messages for instant UI updates
  const [locallyReadMessages, setLocallyReadMessages] = useState<Set<string>>(new Set());
  
  // Milestones for customers
  const [customerMilestones, setCustomerMilestones] = useState<{[customerId: string]: BookingMilestone[]}>({});
  
  // Booking info modal state
  const [bookingInfoModalOpen, setBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Proposal details modal state
  const [proposalDetailsModalOpen, setProposalDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  
  // Pipeline pagination state
  const [pipelineOffset, setPipelineOffset] = useState(20);
  const [loadingMoreCards, setLoadingMoreCards] = useState(false);
  const CARDS_PAGE_SIZE = 20;

  // Mark message as read (for individual message unread badges)
  const markMessageAsRead = async (messageId: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[UNREAD BADGE] Marking message as read:', messageId);

      // Immediately add to locally read set for instant UI update
      setLocallyReadMessages(prev => new Set([...prev, messageId]));

      // Update database
      const { error } = await supabase
        .from('all_messages')
        .update({ read: 'read' })
        .eq('id', messageId);

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        // Remove from locally read set if database update fails
        setLocallyReadMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
        throw error;
      }

      console.log('[UNREAD BADGE] Successfully marked as read:', messageId);
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark all messages in a conversation as read
  const markAllMessagesAsRead = async (card: CommunicationCard) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get all unread message IDs for this conversation
      const unreadMessageIds = card.messages
        .filter(msg => msg.read === 'unread' && msg.direction === 'inbound')
        .map(msg => msg.id);

      if (unreadMessageIds.length === 0) return;

      console.log('[UNREAD BADGE] Marking multiple messages as read:', unreadMessageIds);

      // Immediately add all to locally read set for instant UI update
      setLocallyReadMessages(prev => new Set([...prev, ...unreadMessageIds]));

      // Update database
      const { error } = await supabase
        .from('all_messages')
        .update({ read: 'read' })
        .in('id', unreadMessageIds);

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        // Remove all from locally read set if database update fails
        setLocallyReadMessages(prev => {
          const newSet = new Set(prev);
          unreadMessageIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        throw error;
      }

      console.log('[UNREAD BADGE] Successfully marked multiple as read:', unreadMessageIds);
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Handle edit customer button click
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomerForEdit(customer);
    setEditCustomerModalOpen(true);
  };

  // Handle create customer button click for phone/email
  const handleCreateCustomer = (card: CommunicationCard) => {
    const initialData: { phone?: string; email?: string } = {};
    
    if (card.type === 'phone') {
      initialData.phone = card.contact_info;
    } else if (card.type === 'email') {
      initialData.email = card.contact_info;
    }
    
    setCreateCustomerInitialData(initialData);
    setCreateCustomerModalOpen(true);
  };

  // Handle customer modal success
  const handleCustomerSuccess = (customer: Customer) => {
    // Refresh data to show updated customer info
    if (selectedBusiness?.id) {
      const fetchData = async () => {
        try {
          if (!supabase) {
            throw new Error('Supabase client not initialized');
          }

          // Fetch customers
          const { data: customersData, error: customersError } = await supabase
            .from('customers')
            .select('id, first_name, last_name, email, phone, is_supplier')
            .eq('business_id', selectedBusiness.id);

          if (customersError) throw customersError;
          setCustomers(customersData || []);
        } catch (error) {
          console.error('Error fetching customers:', error);
        }
      };
      fetchData();
    }
    
    // Close modals
    setEditCustomerModalOpen(false);
    setCreateCustomerModalOpen(false);
    setSelectedCustomerForEdit(null);
    setCreateCustomerInitialData({});
  };

  // Handle milestone click to open booking info or proposal details
  const handleMilestoneClick = async (milestone: BookingMilestone) => {
    try {
      // For milestones, the booking_id is stored in the booking_id field
      // But since we're using it for both bookings and proposals, we need to check the metadata
      const entityId = milestone.booking_id;
      
      // Check if this is a proposal milestone
      if (milestone.milestone.metadata?.entity_type === 'proposal') {
        console.log('Loading proposal for milestone:', entityId);
        const proposal = await getProposal(entityId);
        if (proposal) {
          setSelectedProposal(proposal);
          setProposalDetailsModalOpen(true);
        } else {
          console.error('Proposal not found:', entityId);
        }
        return;
      }
      
      // Handle booking milestones
      if (entityId) {
        console.log('Loading booking for milestone:', entityId);
        const booking = await getBooking(entityId);
        if (booking) {
          setSelectedBooking(booking);
          setBookingInfoModalOpen(true);
        } else {
          console.error('Booking not found:', entityId);
        }
      }
    } catch (error) {
      console.error('Error loading entity from milestone:', error);
    }
  };

  // Fetch milestones for a specific customer (lazy loading)
  const fetchCustomerMilestones = async (customerId: string, setLoading?: (loading: boolean) => void) => {
    try {
      console.log('Fetching milestones for customer:', customerId);
      if (setLoading) setLoading(true);
      
      const milestones = await getCustomerMilestones(customerId);
      setCustomerMilestones(prev => ({
        ...prev,
        [customerId]: milestones
      }));
    } catch (error) {
      console.error(`Error fetching milestones for customer ${customerId}:`, error);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Handle booking info modal update
  const handleBookingUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Handle proposal details modal update
  const handleProposalUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Load more cards function
  const loadMoreCards = () => {
    if (loadingMoreCards || !hasMoreCards) return;
    
    setLoadingMoreCards(true);
    
    // Simulate brief loading for better UX
    setTimeout(() => {
      setPipelineOffset(prev => prev + CARDS_PAGE_SIZE);
      setLoadingMoreCards(false);
    }, 300);
  };

  // Reset pipeline pagination when switching tabs
  useEffect(() => {
    setPipelineOffset(CARDS_PAGE_SIZE);
  }, [activeTab]);

  // Fetch messages and customers
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    const fetchData = async () => {
      setLoading(true);
      
      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        // Fetch messages
        const { data: messagesData, error: messagesError } = await supabase
          .from('all_messages')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false });

        if (messagesError) throw messagesError;

        // Fetch customers
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, first_name, last_name, email, phone, is_supplier')
          .eq('business_id', selectedBusiness.id);

        if (customersError) throw customersError;

        setMessages(messagesData || []);
        setCustomers(customersData || []);
        
        // Reset pipeline pagination on fresh data load
        setPipelineOffset(CARDS_PAGE_SIZE);
        
        // Clear locally read messages when fetching fresh data
        setLocallyReadMessages(new Set());
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedBusiness?.id]);

  // Set up realtime subscription for live updates
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    console.log('[REALTIME] Setting up subscription for business:', selectedBusiness.id);

    // Create the subscription
    const subscription = supabase
      .channel(`all_messages_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[REALTIME] Received event:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as Message;
            console.log('[REALTIME] 📥 New message received:', {
              id: newMessage.id,
              type: newMessage.message_type,
              direction: newMessage.direction,
              from: newMessage.from_number,
              to: newMessage.to_number,
              body: newMessage.body.substring(0, 50) + '...',
              timestamp: newMessage.created_at
            });
            
            setMessages(prevMessages => {
              // Check if message already exists (prevent duplicates)
              if (prevMessages.some(msg => msg.id === newMessage.id)) {
                console.log('[REALTIME] ⚠️ Message already exists, skipping:', newMessage.id);
                return prevMessages;
              }
              
              console.log('[REALTIME] ✅ Adding message to state, total messages will be:', prevMessages.length + 1);
              
              // Add new message and sort by created_at (newest first for the state)
              const updatedMessages = [newMessage, ...prevMessages].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              );
              
              return updatedMessages;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as Message;
            console.log('[REALTIME] Updating message:', updatedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedMessage = payload.old as Message;
            console.log('[REALTIME] Removing message:', deletedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.filter(msg => msg.id !== deletedMessage.id)
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('[REALTIME] Subscription status:', status);
      });

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[REALTIME] Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id]);

  // Process messages into communication cards
  useEffect(() => {
    if (!messages.length) {
      setCommunicationCards([]);
      return;
    }

    const cards: { [key: string]: CommunicationCard } = {};

    messages.forEach((message) => {
      let cardId: string;
      let cardType: 'customer' | 'phone' | 'email';
      let contactInfo: string;
      let customerName: string | undefined;

      if (message.customer_id) {
        // Group by customer
        cardId = `customer_${message.customer_id}`;
        cardType = 'customer';
        const customer = customers.find(c => c.id === message.customer_id);
        customerName = customer ? `${customer.first_name} ${customer.last_name}`.trim() : undefined;
        contactInfo = message.message_type === 'sms' 
          ? (message.direction === 'inbound' ? message.from_number : message.to_number)
          : (message.direction === 'inbound' ? message.from_number : message.to_number);
      } else {
        // Group by contact info (phone or email)
        const contact = message.direction === 'inbound' ? message.from_number : message.to_number;
        cardId = `${message.message_type}_${contact}`;
        cardType = message.message_type === 'sms' ? 'phone' : 'email';
        contactInfo = contact;
        customerName = undefined;
      }

      if (!cards[cardId]) {
        cards[cardId] = {
          id: cardId,
          type: cardType,
          customer_id: message.customer_id || undefined,
          customer_name: customerName,
          contact_info: contactInfo,
          messages: [],
          last_message: message,
          unread_count: 0,
          has_unread: false,
        };
      }

      cards[cardId].messages.push(message);
      
      // Update last message if this one is newer
      if (new Date(message.created_at) > new Date(cards[cardId].last_message.created_at)) {
        cards[cardId].last_message = message;
      }

      // Count unread messages (consider both DB state and local state)
      if (message.read === 'unread' && !locallyReadMessages.has(message.id)) {
        cards[cardId].unread_count++;
        cards[cardId].has_unread = true;
      }
    });

    // Convert to array and sort by last message date
    const cardsArray = Object.values(cards).sort(
      (a, b) => new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime()
    );

    console.log('[REALTIME] 📊 Communication cards updated:', {
      totalCards: cardsArray.length,
      selectedCardId: selectedCardIdRef.current,
      selectedCardExists: selectedCardIdRef.current ? cardsArray.some(card => card.id === selectedCardIdRef.current) : false,
      selectedCardMessages: selectedCardIdRef.current ? 
        cardsArray.find(card => card.id === selectedCardIdRef.current)?.messages.length : 'N/A'
    });

    setCommunicationCards(cardsArray);
    
    // Reset pipeline pagination when communication cards change significantly
    const previousCardsLength = communicationCards.length;
    if (Math.abs(cardsArray.length - previousCardsLength) > 5) {
      setPipelineOffset(CARDS_PAGE_SIZE);
    }
  }, [messages, customers, locallyReadMessages]);

  // Keep track of selectedCard ID to avoid stale closure issues
  const selectedCardIdRef = useRef<string | null>(null);
  
  // Update selectedCard ID when it changes
  useEffect(() => {
    selectedCardIdRef.current = selectedCard?.id || null;
  }, [selectedCard]);

  // Update selectedCard when communicationCards change (for real-time updates)
  useEffect(() => {
    const selectedCardId = selectedCardIdRef.current;
    if (selectedCardId && communicationCards.length > 0) {
      const updatedCard = communicationCards.find(card => card.id === selectedCardId);
      const currentSelectedCard = selectedCard;
      
      if (updatedCard && currentSelectedCard) {
        // Check if the number of messages has changed (indicating new messages)
        const hasNewMessages = updatedCard.messages.length !== currentSelectedCard.messages.length;
        const lastMessageChanged = updatedCard.last_message.id !== currentSelectedCard.last_message.id;
        
        console.log('[REALTIME] Checking for updates to selected card:', selectedCardId, {
          hasNewMessages,
          lastMessageChanged,
          currentMessages: currentSelectedCard.messages.length,
          updatedMessages: updatedCard.messages.length,
          currentLastMessage: currentSelectedCard.last_message.id,
          updatedLastMessage: updatedCard.last_message.id
        });
        
        if (hasNewMessages || lastMessageChanged) {
          console.log('[REALTIME] ✅ Updating selected card with new messages:', updatedCard.id, 
            `${currentSelectedCard.messages.length} -> ${updatedCard.messages.length} messages`);
          setSelectedCard(updatedCard);
        }
      }
    }
  }, [communicationCards]); // Remove selectedCard from dependency array to avoid stale closures

  // Filter cards based on active tab and apply pagination
  const allFilteredCards = communicationCards.filter(card => 
    activeTab === 'unread' ? card.has_unread : !card.has_unread
  );
  
  const filteredCards = allFilteredCards.slice(0, pipelineOffset);
  const hasMoreCards = allFilteredCards.length > pipelineOffset;

  const unreadCount = communicationCards.filter(card => card.has_unread).length;

  const CommunicationCardComponent = ({ card }: { card: CommunicationCard }) => {
    const displayName = card.customer_name || card.contact_info;
    const messagePreview = card.last_message.subject || card.last_message.body;
    const timeAgo = new Date(card.last_message.created_at).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const isSelected = selectedCard?.id === card.id;
    
    // Check if this customer is a supplier
    const customer = card.customer_id ? customers.find(c => c.id === card.customer_id) : null;
    const isSupplier = customer?.is_supplier || false;

    const handleMarkAllRead = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection when clicking the button
      markAllMessagesAsRead(card);
    };

    return (
      <div 
        className={`border-b border-gray-200 p-4 cursor-pointer transition-colors ${
          isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
        }`}
        onClick={() => setSelectedCard(card)}
      >
        <div className="flex items-start space-x-3">
          {/* Avatar */}
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            {card.type === 'customer' ? (
              <User className="h-5 w-5 text-blue-600" />
            ) : card.type === 'phone' ? (
              <Phone className="h-5 w-5 text-green-600" />
            ) : (
              <Mail className="h-5 w-5 text-purple-600" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {displayName}
                </p>
                {isSupplier && (
                  <span className="flex items-center space-x-1 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0">
                    <Building2 className="h-3 w-3" />
                    <span>Supplier</span>
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <span className="text-xs text-gray-500">{timeAgo}</span>
                {card.unread_count > 0 && (
                  <span className="bg-blue-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                    {card.unread_count}
                  </span>
                )}
              </div>
            </div>
            
            {/* Message preview */}
            <p className="text-sm text-gray-600 truncate">
              {card.last_message?.message_type === 'email' 
                ? getEmailPreview(card.last_message.body, 80)
                : messagePreview
              }
            </p>

            {/* Message type indicators and Mark Read button */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center space-x-1">
                {card.last_message.message_type === 'sms' ? (
                  <Phone className="h-3 w-3 text-green-500" />
                ) : (
                  <Mail className="h-3 w-3 text-purple-500" />
                )}
                <span className="text-xs text-gray-500 capitalize">
                  {card.last_message.message_type}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500 capitalize">
                  {card.last_message.direction}
                </span>
              </div>
              
              {/* Mark Read Button - only show if there are unread messages */}
              {card.unread_count > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded transition-colors"
                  title={`Mark all ${card.unread_count} message${card.unread_count !== 1 ? 's' : ''} as read`}
                >
                  Mark Read
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ConversationView = ({ card, markMessageAsRead, locallyReadMessages, fetchMilestones }: { 
    card: CommunicationCard; 
    markMessageAsRead: (messageId: string) => Promise<void>;
    locallyReadMessages: Set<string>;
    fetchMilestones: (customerId: string, setLoading?: (loading: boolean) => void) => Promise<void>;
  }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const isFirstRenderRef = useRef(true);
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const locallyReadMessagesRef = useRef<Set<string>>(locallyReadMessages);
    const [replyMethod, setReplyMethod] = useState<'sms' | 'email'>('sms');
    const [messageInput, setMessageInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [newMessageNotification, setNewMessageNotification] = useState(false);
    const [prevMessageCount, setPrevMessageCount] = useState(card.messages.length);
      const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [conversationMessages, setConversationMessages] = useState(card.messages);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messagesOffset, setMessagesOffset] = useState(0);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const displayName = card.customer_name || card.contact_info;
  
  const MESSAGES_PAGE_SIZE = 15;
    
    // Create combined timeline with messages and milestones (memoized for performance)
    const timelineItems = useMemo((): TimelineItem[] => {
      const timeline: TimelineItem[] = [];
      
      // Add messages to timeline
      conversationMessages.forEach(message => {
        timeline.push({
          id: `message_${message.id}`,
          type: 'message',
          created_at: message.created_at,
          message
        });
      });
      
      // Add milestones to timeline (only for customer cards)
      if (card.type === 'customer' && card.customer_id) {
        const milestones = customerMilestones[card.customer_id] || [];
        milestones.forEach(milestone => {
          timeline.push({
            id: `milestone_${milestone.id}`,
            type: 'milestone',
            created_at: milestone.created_at,
            milestone
          });
        });
      }
      
      // Sort timeline by created_at (chronological order)
      return timeline.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    }, [conversationMessages, card.type, card.customer_id, customerMilestones]);

    // Seamless message refresh function
    const refreshConversationMessages = async () => {
      if (!selectedBusiness?.id || !supabase) return;
      
      // Skip refresh if user is actively typing to avoid disruption
      if (isUserTyping) {
        console.log('[REFRESH] ⏸️ Skipping refresh - user is typing');
        return;
      }
      
      try {
        // Determine the filter criteria based on card type
        let whereClause = {};
        
        if (card.type === 'customer' && card.customer_id) {
          whereClause = { customer_id: card.customer_id };
        } else if (card.type === 'phone') {
          whereClause = { 
            customer_id: null, 
            message_type: 'sms',
            // Match both from and to numbers
            or: `from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`
          };
        } else if (card.type === 'email') {
          whereClause = { 
            customer_id: null, 
            message_type: 'email',
            // Match both from and to emails
            or: `from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`
          };
        }

        // Fetch fresh messages for this conversation
        let query = supabase
          .from('all_messages')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false });

        // Apply specific filters based on card type
        if (card.type === 'customer' && card.customer_id) {
          query = query.eq('customer_id', card.customer_id);
        } else {
          query = query.is('customer_id', null);
          if (card.type === 'phone') {
            query = query.eq('message_type', 'sms')
                        .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          } else if (card.type === 'email') {
            query = query.eq('message_type', 'email')
                        .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          }
        }

        const { data: freshMessages, error } = await query;

        if (error) {
          console.error('[REFRESH] Error fetching messages:', error);
          return;
        }

        if (freshMessages) {
          // Check if there are new messages or updates
          const currentMessageIds = new Set(conversationMessages.map(msg => msg.id));
          const newMessages = freshMessages.filter(msg => !currentMessageIds.has(msg.id));
          
          // Always update with fresh data, but preserve local read states
          const locallyReadIds = Array.from(locallyReadMessagesRef.current);
          const updatedMessages = freshMessages.map(freshMsg => {
            // If this message was locally marked as read, preserve that state
            if (locallyReadMessagesRef.current.has(freshMsg.id)) {
              return { ...freshMsg, read: 'read' as const };
            }
            return freshMsg;
          });
          
          if (locallyReadIds.length > 0) {
            console.log('[REFRESH] 🔒 Preserving local read status for messages:', locallyReadIds);
          }
          
          // Check if there are actual changes (new messages or status updates)
          const hasNewMessages = newMessages.length > 0;
          const hasStatusUpdates = JSON.stringify(conversationMessages) !== JSON.stringify(updatedMessages);
          
          if (hasNewMessages || hasStatusUpdates) {
            console.log('[REFRESH] 🔄 Updating conversation:', {
              newMessages: newMessages.length,
              hasStatusUpdates,
              conversationId: card.id
            });
            
            // For real-time updates, we need to handle pagination properly
            if (hasNewMessages) {
              // Add new messages to the current conversation (they should be the most recent)
              const sortedFreshMessages = [...updatedMessages].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              
              // Only show the new messages if they're more recent than what we have
              const latestDisplayedMessage = conversationMessages[conversationMessages.length - 1];
              const newMessagesToAdd = sortedFreshMessages.filter(msg => 
                !latestDisplayedMessage || 
                new Date(msg.created_at).getTime() > new Date(latestDisplayedMessage.created_at).getTime()
              );
              
              if (newMessagesToAdd.length > 0) {
                setConversationMessages(prev => [...prev, ...newMessagesToAdd]);
              }
            } else if (hasStatusUpdates) {
              // For status updates, update the existing messages in place
              setConversationMessages(prev => 
                prev.map(msg => {
                  const updatedMsg = updatedMessages.find(updated => updated.id === msg.id);
                  return updatedMsg || msg;
                })
              );
            }
            
            // Show notification only for new inbound messages
            if (hasNewMessages) {
              const newInboundMessages = newMessages.filter(msg => msg.direction === 'inbound');
              if (newInboundMessages.length > 0) {
                setNewMessageNotification(true);
                setTimeout(() => setNewMessageNotification(false), 3000);
                
                // Auto-scroll or show scroll button
                setTimeout(() => {
                  if (messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
                    
                    if (isNearBottom) {
                      if (messagesEndRef.current) {
                        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                      }
                    } else {
                      setShowScrollToBottom(true);
                    }
                  }
                }, 100);
              }
            }
          }
        }
      } catch (error) {
        console.error('[REFRESH] Error in refresh function:', error);
      }
    };

    // Set up seamless auto-refresh interval
    useEffect(() => {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Set up new interval for this conversation
      refreshIntervalRef.current = setInterval(() => {
        refreshConversationMessages();
      }, 3000); // Refresh every 3 seconds

      // Cleanup on unmount or conversation change
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      };
          }, [card.id, selectedBusiness?.id]);

    // Update locallyReadMessages ref when it changes
    useEffect(() => {
      locallyReadMessagesRef.current = locallyReadMessages;
    }, [locallyReadMessages]);

    // Update conversation messages when card changes - show only recent messages initially
    useEffect(() => {
      const allMessages = [...card.messages].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      // Take only the most recent MESSAGES_PAGE_SIZE messages
      const recentMessages = allMessages.slice(0, MESSAGES_PAGE_SIZE);
      
      // Reverse to show chronologically (oldest first for display)
      setConversationMessages(recentMessages.reverse());
      setPrevMessageCount(recentMessages.length);
      
      // Set pagination state
      setMessagesOffset(MESSAGES_PAGE_SIZE);
      setHasMoreMessages(allMessages.length > MESSAGES_PAGE_SIZE);
    }, [card.id, card.messages]);

    // Fetch milestones when customer conversation is opened (lazy loading, non-blocking)
    useEffect(() => {
      if (card.type === 'customer' && card.customer_id) {
        // Only fetch if we don't already have milestones for this customer
        if (!customerMilestones[card.customer_id]) {
          console.log('Lazy loading milestones for customer:', card.customer_id);
          // Use setTimeout to make this completely non-blocking
          setTimeout(() => {
            if (card.customer_id) {
              fetchMilestones(card.customer_id, setLoadingMilestones);
            }
          }, 100); // Small delay to let the conversation render first
        }
      }
    }, [card.customer_id, card.type]);

    // Load more messages function
    const loadMoreMessages = async () => {
      if (loadingMoreMessages || !hasMoreMessages) return;
      
      setLoadingMoreMessages(true);
      
      try {
        // Get all messages sorted by newest first
        const allMessages = [...card.messages].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        // Get the next batch of messages
        const nextBatch = allMessages.slice(messagesOffset, messagesOffset + MESSAGES_PAGE_SIZE);
        
        if (nextBatch.length > 0) {
          // Reverse the next batch to show chronologically and prepend to current messages
          const nextBatchReversed = nextBatch.reverse();
          setConversationMessages(prev => [...nextBatchReversed, ...prev]);
          
          // Update pagination state
          const newOffset = messagesOffset + MESSAGES_PAGE_SIZE;
          setMessagesOffset(newOffset);
          setHasMoreMessages(allMessages.length > newOffset);
        } else {
          setHasMoreMessages(false);
        }
      } catch (error) {
        console.error('Error loading more messages:', error);
      } finally {
        setLoadingMoreMessages(false);
      }
    };

    // Update timeline when milestones change for this customer
    useEffect(() => {
      // Force timeline update by triggering a re-render when milestones change
      // This is handled automatically since createTimeline() reads from customerMilestones state
    }, [card.customer_id, customerMilestones]);

    // Handle SMS sending
    const handleSendSMS = async () => {
      if (!messageInput.trim()) {
        setSendError('Please enter a message');
        return;
      }

      if (!selectedBusiness?.id) {
        setSendError('No business selected');
        return;
      }

      // Determine the phone number to send to
      let phoneNumber = '';
      if (card.type === 'customer') {
        const customer = customers.find(c => c.id === card.customer_id);
        phoneNumber = customer?.phone || '';
      } else if (card.type === 'phone') {
        phoneNumber = card.contact_info;
      }

      if (!phoneNumber) {
        setSendError('No phone number available for this contact');
        return;
      }

      setIsSending(true);
      setSendError(null);

      try {
        const result = await sendSMS({
          businessId: selectedBusiness.id,
          to: phoneNumber,
          message: messageInput.trim()
        });

        if (result.success) {
          // Clear the input - realtime subscription will handle the message update
          setMessageInput('');
          
          // Mark all messages in this conversation as read since user replied
          markAllMessagesAsRead(card);
        } else {
          setSendError(result.error || 'Failed to send SMS');
        }
      } catch (error) {
        console.error('Error sending SMS:', error);
        setSendError(error instanceof Error ? error.message : 'Failed to send SMS');
      } finally {
        setIsSending(false);
      }
    };

    // Handle email sending success
    const handleEmailSendSuccess = (messageId: string) => {
      console.log('📧 Email sent successfully:', messageId);
      setSendError(null);
      
      // Mark all messages in this conversation as read since user replied
      markAllMessagesAsRead(card);
      
      // Realtime subscription will handle showing the new message
    };

    // Handle email sending error
    const handleEmailSendError = (error: string) => {
      console.error('📧 Email send failed:', error);
      setSendError(error);
    };

    // Get email address for sending
    const getEmailAddress = () => {
      if (card.type === 'customer') {
        const customer = customers.find(c => c.id === card.customer_id);
        return customer?.email || '';
      } else if (card.type === 'email') {
        return card.contact_info;
      }
      return '';
    };

    // Generate default email subject based on conversation
    const getDefaultEmailSubject = () => {
      const lastEmailMessage = [...card.messages]
        .filter(msg => msg.message_type === 'email' && msg.subject)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      
      if (lastEmailMessage?.subject) {
        const subject = lastEmailMessage.subject;
        // Add "Re: " if it doesn't already have it
        if (!subject.toLowerCase().startsWith('re:')) {
          return `Re: ${subject}`;
        }
        return subject;
      }
      
      return 'Reply from your booking';
    };

    // Email composer props
    const emailAddress = getEmailAddress();
    const emailSubject = getDefaultEmailSubject();
    const isEmailDisabled = !selectedBusiness?.id || !emailAddress;
    
    // Handle new message detection and notifications for local state changes
    useEffect(() => {
      if (conversationMessages.length > prevMessageCount) {
        const newMessagesCount = conversationMessages.length - prevMessageCount;
        console.log('[CONVERSATION] Local message count changed:', prevMessageCount, '->', conversationMessages.length);
        
        // Auto-scroll if user is near bottom
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
          
          if (isNearBottom) {
            setTimeout(() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        }
      }
      setPrevMessageCount(conversationMessages.length);
    }, [conversationMessages.length, prevMessageCount]);

    // Auto-scroll to bottom when conversation changes or on first render
    useEffect(() => {
      if (messagesEndRef.current) {
        // Instant scroll for first render/conversation changes
        const behavior = isFirstRenderRef.current ? 'auto' : 'smooth';
        messagesEndRef.current.scrollIntoView({ behavior });
        isFirstRenderRef.current = false;
      }
    }, [card.id]);

    // Handle scroll to detect if user is at bottom
    const handleScroll = () => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
        
        if (isNearBottom) {
          setShowScrollToBottom(false);
        }
      }
    };

    // Scroll to bottom function
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        setShowScrollToBottom(false);
      }
    };

    // Add scroll listener to messages container
    useEffect(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, []);

    // Reset first render flag and message tracking when conversation changes
    useEffect(() => {
      isFirstRenderRef.current = true;
      setPrevMessageCount(conversationMessages.length);
      setNewMessageNotification(false);
      setShowScrollToBottom(false);
      setLoadingMoreMessages(false);
    }, [card.id]);

    // Set reply method based on customer's last message type
    useEffect(() => {
      setReplyMethod(card.last_message.message_type);
    }, [card.id, card.last_message.message_type]);

    // Clear message input and error when conversation changes
    useEffect(() => {
      setMessageInput('');
      setSendError(null);
    }, [card.id]);

    // Handle typing detection for SMS input
    const handleMessageInputChange = (value: string) => {
      setMessageInput(value);
      
      // Set typing state
      setIsUserTyping(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout to stop typing state after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 2000);
    };

    // Clear error when user starts typing
    useEffect(() => {
      if (messageInput && sendError) {
        setSendError(null);
      }
    }, [messageInput, sendError]);

    // Get available reply methods based on card type and customer contact info
    const getAvailableReplyMethods = () => {
      if (card.type === 'customer') {
        const customer = customers.find(c => c.id === card.customer_id);
        const methods = [];
        if (customer?.phone) methods.push('sms');
        if (customer?.email) methods.push('email');
        return methods;
      } else if (card.type === 'phone') {
        return ['sms'];
      } else if (card.type === 'email') {
        return ['email'];
      }
      return [];
    };

    const availableReplyMethods = getAvailableReplyMethods();

    // Update reply method if current selection is not available
    useEffect(() => {
      if (availableReplyMethods.length > 0 && !availableReplyMethods.includes(replyMethod)) {
        // Set to the last message type if available, otherwise first available method
        if (availableReplyMethods.includes(card.last_message.message_type)) {
          setReplyMethod(card.last_message.message_type);
        } else {
          setReplyMethod(availableReplyMethods[0] as 'sms' | 'email');
        }
      }
    }, [card.id, availableReplyMethods, replyMethod, card.last_message.message_type]);

    return (
      <div className="flex flex-col h-full">
        {/* Conversation Header - Fixed */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3">
            {/* Mobile Back Button */}
            <button
              onClick={() => setSelectedCard(null)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <span className="sr-only">Back to conversations</span>
              ←
            </button>
            
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              {card.type === 'customer' ? (
                <User className="h-5 w-5 text-blue-600" />
              ) : card.type === 'phone' ? (
                <Phone className="h-5 w-5 text-green-600" />
              ) : (
                <Mail className="h-5 w-5 text-purple-600" />
              )}
            </div>
                        <div className="flex-1 min-w-0">
              {card.type === 'customer' ? (
                /* Customer card - show name and available contact info */
                <>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">{displayName}</h3>
                    {(() => {
                      const customer = customers.find(c => c.id === card.customer_id);
                      return customer?.is_supplier && (
                        <span className="flex items-center space-x-1 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                          <Building2 className="h-3 w-3" />
                          <span>Supplier</span>
                        </span>
                      );
                    })()}
                    <button
                      onClick={() => {
                        const customer = customers.find(c => c.id === card.customer_id);
                        if (customer) handleEditCustomer(customer);
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Edit customer"
                    >
                      <Edit className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {(() => {
                      const customer = customers.find(c => c.id === card.customer_id);
                      return (
                        <>
                          {customer?.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="h-3 w-3 text-green-500" />
                              <p className="text-sm text-gray-600 truncate">{customer.phone}</p>
                            </div>
                          )}
                          {customer?.email && (
                            <div className="flex items-center space-x-2">
                              <Mail className="h-3 w-3 text-purple-500" />
                              <p className="text-sm text-gray-600 truncate">{customer.email}</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                /* Phone or Email only card - show just the contact info */
                <>
                  <div className="flex items-center space-x-2">
                    {card.type === 'phone' ? (
                      <Phone className="h-4 w-4 text-green-500" />
                    ) : (
                      <Mail className="h-4 w-4 text-purple-500" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 truncate">{card.contact_info}</h3>
                    <button
                      onClick={() => handleCreateCustomer(card)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Create customer"
                    >
                      <Plus className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Desktop Close Button */}
            <button
              onClick={() => setSelectedCard(null)}
              className="hidden md:block p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <span className="sr-only">Close conversation</span>
              ✕
            </button>
          </div>
        </div>

        {/* Messages - Scrollable */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 space-y-4 relative">
          {/* New Message Notification */}
          {newMessageNotification && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg animate-pulse">
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-4 w-4" />
                <span className="text-sm font-medium">New message received</span>
              </div>
            </div>
          )}
          
          {/* Show More Messages Button */}
          {hasMoreMessages && (
            <div className="flex justify-center py-3">
              <button
                onClick={loadMoreMessages}
                disabled={loadingMoreMessages}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                {loadingMoreMessages ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Show More Messages</span>
                )}
              </button>
            </div>
          )}
          
          {/* Loading milestones indicator */}
          {loadingMilestones && card.type === 'customer' && (
            <div className="flex justify-center py-2">
              <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full flex items-center space-x-2">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-400 border-t-transparent"></div>
                <span>Loading milestones...</span>
              </div>
            </div>
          )}
          
          {timelineItems.map((item) => {
            if (item.type === 'message' && item.message) {
              const message = item.message;
              const isOutbound = message.direction === 'outbound';
              const messageTime = new Date(message.created_at).toLocaleString();
              
              // Check if message is unread (not outbound AND not read in DB AND not locally marked as read)
              const isUnread = !isOutbound && 
                             message.read === 'unread' && 
                             !locallyReadMessages.has(message.id);
              
              return (
                <div
                  key={item.id}
                  className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="relative">
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isOutbound
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      {/* Message Type & Subject */}
                      <div className="flex items-center space-x-2 mb-1">
                        {message.message_type === 'sms' ? (
                          <Phone className="h-3 w-3" />
                        ) : (
                          <Mail className="h-3 w-3" />
                        )}
                        <span className="text-xs font-medium">
                          {message.message_type.toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Email Subject */}
                      {message.subject && (
                        <div className="font-medium text-sm mb-1">
                          {message.subject}
                        </div>
                      )}
                      
                      {/* Message Body */}
                      <div className="text-sm whitespace-pre-wrap break-words">
                        {message.message_type === 'email' 
                          ? parseEmailContent(message.body)
                          : message.body
                        }
                      </div>
                      
                      {/* Timestamp */}
                      <div className={`text-xs mt-2 ${
                        isOutbound ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {messageTime}
                      </div>
                    </div>
                    
                    {/* Unread Badge - "Unread" text badge positioned outside the message bubble */}
                    {isUnread && (
                      <button
                        onClick={() => markMessageAsRead(message.id)}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-full transition-colors cursor-pointer shadow-lg"
                        title="Mark as read"
                      >
                        Unread
                      </button>
                    )}
                  </div>
                </div>
              );
            } else if (item.type === 'milestone' && item.milestone) {
              const milestone = item.milestone;
              const milestoneTime = new Date(milestone.created_at).toLocaleString();
              
              // Helper function to get milestone icon - professional neutral styling
              const getMilestoneIcon = (type: string, title?: string) => {
                const iconClass = "h-4 w-4 text-slate-600";
                
                switch (type) {
                  case 'booking_scheduled':
                    return <Calendar className={iconClass} />;
                  case 'booking_completed':
                    return <CheckCircle className={iconClass} />;
                  case 'proposal_created':
                    return <FileText className={iconClass} />;
                  case 'custom':
                    // Check milestone title to determine icon
                    if (title?.includes('Proposal Sent')) {
                      return <Mail className={iconClass} />;
                    } else if (title?.includes('Proposal Viewed')) {
                      return <Eye className={iconClass} />;
                    } else if (title?.includes('Proposal Approved')) {
                      return <CheckCircle className={iconClass} />;
                    }
                    return <CheckCircle className={iconClass} />;
                  default:
                    return <CheckCircle className={iconClass} />;
                }
              };
              
              // Helper function to get milestone color - professional neutral styling
              const getMilestoneColor = () => {
                return 'bg-slate-50 text-slate-700 border-slate-200 shadow-sm';
              };
              
              return (
                <div key={item.id} className="flex justify-end">
                  <div 
                    className={`max-w-sm px-4 py-3 rounded-lg border cursor-pointer hover:shadow-md transition-all duration-200 ${getMilestoneColor()}`}
                    onClick={() => handleMilestoneClick(milestone)}
                    title={milestone.milestone.metadata?.entity_type === 'proposal' ? 'Click to view proposal details' : 'Click to view booking details'}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200">
                          {getMilestoneIcon(milestone.milestone.type, milestone.milestone.title)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-800 mb-1">
                          {milestone.milestone.title}
                        </div>
                        {milestone.milestone.description && (
                          <div className="text-xs text-slate-600 leading-relaxed mb-2">
                            {milestone.milestone.description}
                          </div>
                        )}
                        <div className="text-xs text-slate-500 font-medium">
                          {milestoneTime}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            
            return null;
          })}
          
          {/* Scroll to Bottom Button */}
          {showScrollToBottom && (
            <button
              onClick={scrollToBottom}
              className="fixed bottom-32 right-8 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg z-20 transition-all duration-200 animate-bounce"
              title="Scroll to bottom"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          )}
          
          {/* Auto-scroll target - always at bottom */}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Method Toggle - Fixed above reply bar */}
        <div className="flex-shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Reply via:</span>
            {availableReplyMethods.length > 1 ? (
              <div className="flex bg-white rounded-lg border border-gray-300 p-1">
                {availableReplyMethods.includes('sms') && (
                  <button
                    onClick={() => setReplyMethod('sms')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      replyMethod === 'sms'
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Phone className="h-4 w-4" />
                    <span>SMS</span>
                  </button>
                )}
                {availableReplyMethods.includes('email') && (
                  <button
                    onClick={() => setReplyMethod('email')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      replyMethod === 'email'
                        ? 'bg-purple-100 text-purple-700 border border-purple-300'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </button>
                )}
              </div>
            ) : availableReplyMethods.length === 1 ? (
              <div className="flex bg-white rounded-lg border border-gray-300 p-1">
                <div className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium ${
                  replyMethod === 'sms'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-purple-100 text-purple-700 border border-purple-300'
                }`}>
                  {replyMethod === 'sms' ? (
                    <>
                      <Phone className="h-4 w-4" />
                      <span>SMS</span>
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No communication methods available</div>
            )}
          </div>
        </div>

        {/* Message Input Area - Fixed at bottom */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white shadow-lg">
          {replyMethod === 'email' ? (
            /* Email Reply Composer */
            <div className="w-full">
              <EmailReplyComposer
                businessId={selectedBusiness?.id || ''}
                to={emailAddress}
                defaultSubject={emailSubject}
                placeholder="Type your email message here..."
                onSendSuccess={handleEmailSendSuccess}
                onSendError={handleEmailSendError}
                disabled={isEmailDisabled}
                className="w-full"
              />
            </div>
          ) : (
            /* SMS Reply Input */
            <>
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <textarea
                    placeholder="Type your SMS message here..."
                    className="w-full resize-none border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={2}
                    value={messageInput}
                    onChange={(e) => handleMessageInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!isSending && messageInput.trim()) {
                          handleSendSMS();
                        }
                      }
                    }}
                  />
                </div>
                <div className="flex flex-col space-y-2 flex-shrink-0">
                  <button
                    disabled={isSending || !messageInput.trim()}
                    onClick={handleSendSMS}
                    className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                      isSending || !messageInput.trim()
                        ? 'bg-green-200 text-green-600 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
                    }`}
                    title={isSending ? 'Sending...' : 'Send SMS (Enter)'}
                  >
                    {isSending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent"></div>
                    ) : (
                      <Phone className="h-4 w-4" />
                    )}
                    <span>{isSending ? 'Sending...' : 'Send SMS'}</span>
                  </button>
                </div>
              </div>
              {sendError && (
                <div className="mt-2 text-xs text-red-500 text-center">
                  {sendError}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-6rem)] bg-gray-50 flex items-center justify-center overflow-hidden p-0">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-6rem)] bg-gray-50 flex flex-col p-0">
      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Message Pipeline */}
        <div className={`${
          selectedCard ? 'hidden md:flex' : 'flex'
        } w-full md:w-96 lg:w-[500px] bg-white border-r border-gray-200 flex-col h-full`}>
          {/* Pipeline Header */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Message Pipeline</h2>
            <p className="text-sm text-gray-500 mt-1">Manage your conversations</p>
          </div>

          {/* Pipeline Tabs */}
          <div className="flex-shrink-0 flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('unread')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'unread'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>Unread</span>
                {unreadCount > 0 && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('read')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'read'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center">
                <span>Read</span>
              </div>
            </button>
          </div>

          {/* Pipeline Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {filteredCards.length > 0 ? (
              <>
                <div className="divide-y divide-gray-200">
                  {filteredCards.map((card) => (
                    <CommunicationCardComponent key={card.id} card={card} />
                  ))}
                </div>
                
                {/* Load More Cards Button */}
                {hasMoreCards && (
                  <div className="flex justify-center p-4 border-t border-gray-200">
                    <button
                      onClick={loadMoreCards}
                      disabled={loadingMoreCards}
                      className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                    >
                      {loadingMoreCards ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <span>Load More ({allFilteredCards.length - pipelineOffset} remaining)</span>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full px-6 py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  {activeTab === 'unread' ? (
                    <Mail className="h-8 w-8 text-gray-400" />
                  ) : (
                    <MessageCircle className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <p className="text-gray-500 text-center">
                  No {activeTab} messages
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Conversation View */}
        <div className={`${
          selectedCard ? 'flex' : 'hidden md:flex'
        } flex-1 flex-col bg-gray-50 h-full`}>
          {selectedCard ? (
            <ConversationView 
              card={selectedCard} 
              markMessageAsRead={markMessageAsRead}
              locallyReadMessages={locallyReadMessages}
              fetchMilestones={fetchCustomerMilestones}
            />
          ) : (
            /* No Conversation Selected State */
            <div className="flex-1 flex items-center justify-center px-6 py-0">
              <div className="text-center max-w-sm">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  No Conversation Selected
                </h3>
                <p className="text-gray-500">
                  Select a conversation from the pipeline to view messages here
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Customer Modals */}
      {selectedBusiness && (
        <>
          <EditCustomerModal
            isOpen={editCustomerModalOpen}
            onClose={() => {
              setEditCustomerModalOpen(false);
              setSelectedCustomerForEdit(null);
            }}
            onSuccess={handleCustomerSuccess}
            customer={selectedCustomerForEdit}
          />
          
          <CustomerModal
            isOpen={createCustomerModalOpen}
            onClose={() => {
              setCreateCustomerModalOpen(false);
              setCreateCustomerInitialData({});
            }}
            onSuccess={handleCustomerSuccess}
            businessId={selectedBusiness.id}
            initialData={createCustomerInitialData}
          />
          
          <BookingInfoModal
            isOpen={bookingInfoModalOpen}
            onClose={() => {
              setBookingInfoModalOpen(false);
              setSelectedBooking(null);
            }}
            booking={selectedBooking}
            onUpdate={handleBookingUpdate}
            readOnly={false}
          />
          
          {selectedProposal && (
            <ProposalDetailsModal
              isOpen={proposalDetailsModalOpen}
              onClose={() => {
                setProposalDetailsModalOpen(false);
                setSelectedProposal(null);
              }}
              proposal={selectedProposal}
              onUpdate={handleProposalUpdate}
            />
          )}
        </>
      )}
    </div>
  );
} 